import { useState, useEffect } from "react";
import "./Subscribers.css";
import Header from "../../Components/Header/Header";
import { useUser } from "../../Context/UserContext";
import { callApi } from "../../utils/apiService";
import { videoCreatorEndPoint } from "../../Constants/constants";
import PaginationLeft from "../../assets/images/pagination-left-arrow.svg";
import PaginationRight from "../../assets/images/pagination-right-arrow.svg";
import dayjs from "dayjs";

const PER_PAGE = 10;

function getInitials(name = "") {
  return name
    .trim()
    .split(" ")
    .filter(Boolean)
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function Avatar({ src, name, colorIndex }) {
  const [failed, setFailed] = useState(false);

  if (!src || failed) {
    return (
      <div
        className="sub-avatar sub-avatar--fallback"
      >
        {getInitials(name) || ""}
      </div>
    );
  }

  return (
    <img
      className="sub-avatar"
      src={src}
      alt={name}
      onError={() => setFailed(true)}
    />
  );
}

function SubscriberRow({ subscriber, index }) {
  return (
    <div className="sub-row">
      <Avatar
        src={subscriber.subscriberProfileUrl}
        name={subscriber.subscriberUserName}
        colorIndex={index}
      />
      <div className="sub-info">
        <p className="sub-name">{subscriber.subscriberUserName}</p>
        <p className="sub-meta">{subscriber.followersCount ? subscriber.followersCount : 0} Followers</p>
        <p className="sub-date">{dayjs(subscriber.subscribedAt).format("D MMM [at] h:mm A")}</p>
      </div>
    </div>
  );
}

function Pagination({ currentPage, totalPages, onPageChange, pageNumbers }) {
  return (
    <div className="pagination-controls">
      <img
        src={PaginationLeft}
        alt="Left Arrow"
        style={{
          width: "32px",
          height: "32px",
          cursor: currentPage > 1 ? "pointer" : "not-allowed",
          opacity: currentPage > 1 ? 1 : 0.5,
        }}
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
      />

      {pageNumbers.map((page) => (
        <button
          key={page}
          className={`content-pagination-btn page-btn ${
            currentPage === page ? "active" : ""
          }`}
          onClick={() => onPageChange(page)}
        >
          {page}
        </button>
      ))}

      <img
        src={PaginationRight}
        alt="Right Arrow"
        style={{
          width: "32px",
          height: "32px",
          cursor: currentPage < totalPages ? "pointer" : "not-allowed",
          opacity: currentPage < totalPages ? 1 : 0.5,
        }}
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      />
    </div>
  );
}

export default function Subscribers() {
  const ITEMS_PER_PAGE = 10;

  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const { user } = useUser();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const showingFrom =
    totalItems === 0 ? 0 : (currentPage - 1) * ITEMS_PER_PAGE + 1;

  const showingTo = Math.min(currentPage * ITEMS_PER_PAGE, totalItems);
  const fetchSubscribers = async () => {
    try {
      setLoading(true);

      const response = await callApi(
        `${videoCreatorEndPoint}subscribe/subscribers?page=${currentPage}&limit=${ITEMS_PER_PAGE}`,
        "GET",
      );

      if (response?.status) {
        setSubscribers(response?.data?.subscribers || []);
        setTotalItems(response?.data?.pagination?.totalCount || 0);
        setTotalPages(response?.data?.pagination?.totalPages || 0);
      }
    } catch (error) {
      console.error("Subscribers API Error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscribers();
  }, [currentPage]);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const pageNumbers = Array.from(
    { length: totalPages },
    (_, index) => index + 1,
  );

  return (
    <div className="dashboard">
      <div className="dashboard-bg-stripe"></div>
      <Header />
      <div className="page-content">
        <h1 className="page-title">Your Subscribers</h1>
        <p className="page-subtitle">{totalItems} people are following you</p>

        <div className="card">
          <div className="card-header">{user?.fullName}'s Subscribers</div>

          <div className="sub-list">
            {subscribers.length > 0 ? (
              subscribers.map((subscriber, i) => (
                <SubscriberRow
                  key={subscriber.id}
                  subscriber={subscriber}
                  index={i}
                />
              ))
            ) : (
              <div className="no-data">No subscribers found</div>
            )}
          </div>
          <div className="pagination-wrapper">
            <span className="content-pagination-info">
              Showing
              <span className="content-pagination-count">
                {" "}
                {showingFrom}-{showingTo}
              </span>{" "}
              from
              <span className="content-pagination-count">
                {" "}
                {totalItems}
              </span>{" "}
              subscribers
            </span>

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              pageNumbers={pageNumbers}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
